import React, { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Chip from '@mui/material/Chip';
import { listHomeworkAggregateRange } from '../api/homeworks';

// Istanbul date formatting (ISO yyyy-mm-dd)
const todayTR = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

function isoNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' });
  return fmt.format(d);
}

export default function HomeworkAggregateSummary({ schoolId, onError }) {
  const [start, setStart] = useState(isoNDaysAgo(6)); // last 7 days default
  const [end, setEnd] = useState(todayTR);
  const [className, setClassName] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const load = useCallback(async () => {
    if (!schoolId || !start || !end) return;
    setLoading(true);
    try {
      const data = await listHomeworkAggregateRange(schoolId, start, end, className || undefined);
      const items = Array.isArray(data?.items) ? data.items : [];
      // Sort: highest done_count desc, then surname asc
      items.sort((a, b) => {
        const diff = (b.done_count || 0) - (a.done_count || 0);
        if (diff !== 0) return diff;
        return (a.surname || '').localeCompare(b.surname || '');
      });
      setRows(items);
    } catch (e) {
      onError?.('Ödev özeti getirilemedi');
    } finally {
      setLoading(false);
    }
  }, [schoolId, start, end, className, onError]);

  useEffect(() => { load(); }, [load]);

  const quick7 = () => { setStart(isoNDaysAgo(6)); setEnd(todayTR); };
  const quick30 = () => { setStart(isoNDaysAgo(29)); setEnd(todayTR); };

  const isRange7 = start === isoNDaysAgo(6) && end === todayTR;
  const isRange30 = start === isoNDaysAgo(29) && end === todayTR;

  return (
    <Box>
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Ödev Durumu Özeti (Tüm Öğrenciler)</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={2} sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={isRange7 ? 'contained' : 'outlined'}
              color={isRange7 ? 'primary' : 'inherit'}
              onClick={quick7}
              fullWidth
            >Son 7 Gün</Button>
            <Button
              variant={isRange30 ? 'contained' : 'outlined'}
              color={isRange30 ? 'primary' : 'inherit'}
              onClick={quick30}
              fullWidth
            >Son 30 Gün</Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField type="date" label="Başlangıç" value={start} onChange={(e)=>setStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField type="date" label="Bitiş" value={end} onChange={(e)=>setEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Şube (opsiyonel)" value={className} onChange={(e)=>setClassName(e.target.value)} placeholder="8A" fullWidth />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {isMobile ? null : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(240px,1fr) 90px 90px 90px 90px 110px 130px',
            columnGap: 2,
            px: 2,
            py: 1.2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: 1,
            alignItems: 'center'
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Öğrenci</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>Yapılan</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>Eksik</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>Geç</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>Kayıtsız</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>Tamamlama %</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>İşlemler</Typography>
          </Box>
        )}
        {loading ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={isMobile ? 96 : 44} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : isMobile ? (
          <Box sx={{ maxHeight: 520, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            {(rows || []).map((r, idx) => {
              const done = r.done_count || 0;
              const missing = r.missing_count || 0;
              const late = r.late_count || 0;
              const unrec = r.unrecorded_count != null ? r.unrecorded_count : Math.max((r.total_count || 0) - (done + missing + late), 0);
              const total = r.total_count || (done + missing + late);
              const effectiveTotal = Math.max(total - unrec, 0);
              const pct = effectiveTotal > 0 ? Math.round((done / effectiveTotal) * 100) : 0;
              return (
                <Box key={r.id} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderBottomColor: 'divider', bgcolor: idx % 2 === 0 ? 'background.default' : 'background.paper' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.name} {r.surname}{r.class_name ? ` • ${r.class_name}` : ''}</Typography>
                    <Button size="small" variant="outlined" onClick={() => window.open(`/admin/student360?student_id=${encodeURIComponent(r.id)}`, '_blank')}>360</Button>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 1 }}>
                    <Chip label={`Yapılan: ${done}`} size="small" />
                    <Chip label={`Eksik: ${missing}`} size="small" />
                    <Chip label={`Yok: ${late}`} size="small" />
                    <Chip label={`Kayıtsız: ${unrec}`} size="small" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip label={`Tamamlama: %${pct}`} size="small" color={pct >= 70 ? 'success' : pct >= 40 ? 'warning' : 'default'} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
            {(rows || []).map((r, idx) => {
              const done = r.done_count || 0;
              const missing = r.missing_count || 0;
              const late = r.late_count || 0;
              const unrec = r.unrecorded_count != null ? r.unrecorded_count : Math.max((r.total_count || 0) - (done + missing + late), 0);
              const total = r.total_count || (done + missing + late);
              const effectiveTotal = Math.max(total - unrec, 0);
              const pct = effectiveTotal > 0 ? Math.round((done / effectiveTotal) * 100) : 0;
              return (
                <Box key={r.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(240px,1fr) 90px 90px 90px 90px 110px 130px',
                    columnGap: 2,
                    px: 2,
                    py: 1.2,
                    bgcolor: idx % 2 === 0 ? 'background.default' : 'background.paper',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    alignItems: 'center'
                  }}>
                  <Typography variant="body2" sx={{ pr: 1 }}>{r.name} {r.surname} {r.class_name ? `• ${r.class_name}` : ''}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>{done}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>{missing}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>{late}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>{unrec}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>{pct}%</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Button size="small" variant="outlined" onClick={() => window.open(`/admin/student360?student_id=${encodeURIComponent(r.id)}`, '_blank')}>öğrenci360</Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
